import React, { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ukDayTime, ukDayYear } from "@/lib/time";
import { caseState, laneStage, gbp, ladderFor } from "@/lib/stages";
import {
  Btn, CellSub, Conf, DateCell, DeskAlias, DeskSupport, Divider, Empty, Kv, Ladder, LinkRow, Money, Note, PageBody, PageHead, Panel, RailCard, RangeBar, Sec, St, StatusChip, Tabs, Tb, Td, Th,
} from "@/components/portal/crm/kit";
import { CaseThread } from "@/components/portal/crm/CaseThread";
import { CaseStatusBanner } from "@/components/portal/crm/CaseStatusBanner";
import { DeskLetters, said } from "@/components/portal/crm/DeskLetters";
import { LiveLine } from "@/components/portal/crm/LiveLine";
import { QuickReply } from "@/components/portal/crm/QuickReply";
import { UpdateHistory } from "@/components/portal/crm/UpdateHistory";
import { DocRequests } from "@/components/portal/DocRequests";
import { DocUploader, prettySize } from "@/components/portal/DocUploader";
import { I } from "@/components/portal/sys/icons";
import { DocsProgress } from "@/components/portal/crm/DocsProgress";
import { CompanyCheckCard } from "@/components/portal/crm/company/CompanyCheckCard";
import { CompanyCheckReport } from "@/components/portal/crm/company/CompanyCheckReport";
import { useCompanyCheck } from "@/lib/companyCheck";
import { FastDocs } from "@/components/portal/FastDocs";
import { fastDocsMissing, sendFile } from "@/lib/fastDocs";
import { toast } from "sonner";


export default function CaseRecord() {
  const { id } = useParams();
  const { leads, reload, isAdmin, projection, agent, me } = useOutletContext();
  const nav = useNavigate();
  const lead = (leads || []).find((l) => l.id === id);

  const [panel, setPanel] = useState(null);
  const [tab, setTab] = useState("thread");
  const [files, setFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [attaching, setAttaching] = useState(false);
  const [chasing, setChasing] = useState(false);
  const [chased, setChased] = useState(false);
  const [tick, setTick] = useState(0);
  const cc = useCompanyCheck(id, "referrer");
  const hasCheck = !!cc.data && !["none", "skipped"].includes(cc.data.status);
  const openReport = () => { setTab("company"); document.getElementById("referrer-case-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  useEffect(() => { setFiles(lead?.files || []); }, [lead]);
  useEffect(() => { if (panel?.your_chase?.cooling) setChased(true); }, [panel]);
  useEffect(() => {
    let alive = true;
    const pull = () => api.get(`/refer/leads/${id}/panel`)
      .then((r) => { if (alive) setPanel(r.data); })
      .catch(() => { if (alive) setPanel(null); });
    pull();
    const t = setInterval(pull, 12000);
    return () => { alive = false; clearInterval(t); };
  }, [id, tick]);

  const attach = async () => {
    if (!newFiles.length) return;
    setAttaching(true);
    try {
      const r = await api.post(`/refer/leads/${id}/files`, { file_ids: newFiles.map((f) => f.id) });
      setFiles(r.data.files || []);
      setNewFiles([]);
      toast.success("Documents attached — the desk and the lenders holding it can see them now");
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Attach failed"); }
    finally { setAttaching(false); }
  };

  const chase = async () => {
    setChasing(true);
    try {
      const r = await api.post(`/refer/leads/${id}/chase`, {});
      setChased(true);
      toast.success(r.data?.message || "We've got it — the desk will come back to you today");
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (e?.response?.status === 429) setChased(true);
      toast[e?.response?.status === 429 ? "info" : "error"](msg || "That didn't go through");
    } finally { setChasing(false); }
  };

  const desks = panel?.desks || [];

  if (!lead) {
    return (
      <Panel testId="referrer-case-missing">
        <Empty>
          That case isn&apos;t on your book.
          <div style={{ marginTop: 16 }}><Btn as={Link} to="/introducer">Back to my cases</Btn></div>
        </Empty>
      </Panel>
    );
  }

  const st = caseState(lead);
  const p = panel?.projection || (projection?.cases || []).find((c) => c.lead_id === id);
  const offers = panel?.offers || [];
  const best = offers[0];
  const live = desks.filter((d) => !["declined"].includes(d.status));
  const passed = desks.filter((d) => d.status === "declined");
  const declined = lead.stage === "declined";
  const declinedOrDone = declined || lead.stage === "completed";
  const withdrawn = desks.filter((d) => d.withdrawn);

  const rail = (
    <>
      {!agent && <Panel title="Your fee on this case" testId="referrer-case-range">
        <div className="money" style={{ fontSize: 30 }} data-testid="referrer-case-fee">{gbp(p?.likely_gbp)}</div>
        <div className="money-sub">
          {p?.state === "funded" ? "Confirmed — payable the month after completion"
            : p?.state === "offer" && p?.single ? "A single figure now terms are on the table" : "Payable the month after completion"}
        </div>
        {p && !p.single && (
          <>
            <RangeBar min={p.min_gbp} likely={p.likely_gbp} max={p.max_gbp} labels={false} testId="referrer-case-rangebar" />
            <div className="ladder-lab" style={{ marginTop: 0 }}><span>{gbp(p.min_gbp)}</span><span>{gbp(p.max_gbp)}</span></div>
          </>
        )}
        <Divider style={{ margin: "18px 0" }} />
        <Kv rows={[
          ["Facility on the table", gbp(best?.amount_gbp || lead.amount_gbp)],
          ["Your rate", projection?.model?.rate || "—"],
          ["Desks live", `${live.length} of ${desks.length || "—"}`],
        ]} />
        <p style={{ color: "var(--mut)", fontSize: 12.5, margin: "16px 0 0" }}>{p?.basis}</p>
        <Divider style={{ margin: "18px 0" }} />
        <LinkRow label="See the arithmetic" onClick={() => nav("/introducer/performance")} />
      </Panel>}

      <DeskSupport body={agent ? "Handling this case end to end. Ask anything in the thread below." : "Handling this case end to end."} testId="referrer-case-support" chat={!agent} />

      <RailCard title="The business" testId="referrer-case-business">
        <Kv rows={[
          ["Applicant", lead.applicant_name],
          ...(lead.submitted_by_name ? [["Put in by", lead.submitted_by_name]] : []),
          ["Sector", lead.industry_sector || "—"],
          ["Finance", lead.finance_type],
          ["Term", lead.term_months ? `${lead.term_months} mo` : "—"],
          ["Urgency", (lead.urgency || "—").replace(/_/g, " ")],
        ]} />
      </RailCard>

      <RailCard title="Documents" testId="referrer-case-doclinks">
        {files.length === 0
          ? <div style={{ fontSize: 13, color: "var(--mut)" }}>Nothing attached yet.</div>
          : files.slice(0, 5).map((f) => (
            <LinkRow key={f.id} label={f.filename} onClick={() => setTab("documents")} testId={`referrer-doclink-${f.id}`} />
          ))}
      </RailCard>

      <Note>{panel?.note}</Note>
    </>
  );

  return (
    <>
      <button onClick={() => nav("/introducer")} data-testid="referrer-case-back"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, border: 0, background: "none", cursor: "pointer",
                font: '700 12.5px "Instrument Sans"', color: "var(--mut)", padding: 0, marginBottom: 14, whiteSpace: "nowrap" }}>
        <I.back /> My cases
      </button>

      <PageHead
        eyebrow={lead.reference ? `Case ${lead.reference}` : "Case record"}
        title={lead.business_name || lead.applicant_name}
        testId="referrer-lead-detail"
        meta={
          <>
            <StatusChip state={st} testId="referrer-case-stage" />
            {st.key === "awaiting_docs" && <DocsProgress progress={lead.docs_progress} variant="inline" testId="referrer-case-docs-progress" />}
            <span style={{ color: "var(--mut)", fontSize: 13 }}>{lead.finance_type} · {gbp(lead.amount_gbp)}</span>
            <span style={{ width: 1, height: 16, background: "var(--line-2)" }} />
            <span style={{ color: "var(--mut)", fontSize: 13 }}>Introduced {ukDayYear(lead.created_at, "—")}</span>
          </>
        }
        actions={
          <>
            {!declinedOrDone && (
              <Btn onClick={chase} disabled={chasing || chased} testId="referrer-case-chase"
                   title={chased ? "You've asked us today — we'll come back to you" : "Ask the desk where this is up to"}>
                {chasing ? "Asking…" : chased ? "We're on it" : "Chase for an update"}
              </Btn>
            )}
            <Btn onClick={() => setTab("thread")} testId="referrer-case-addnote">Add to the thread</Btn>
            {!agent && <Btn kind="primary" onClick={() => window.dispatchEvent(new CustomEvent("portal-chat:open"))}>Message the desk</Btn>}
          </>
        }
      />

      <PageBody rail={rail}>
        <CaseStatusBanner lead={lead} panel={panel} onChase={chase} chasing={chasing} chased={chased} agent={agent} />
        <QuickReply leadId={lead.id} desks={desks} onSent={() => setTick((n) => n + 1)} />

        <div className="card">
          <div className="card-b">
            <Ladder steps={ladderFor(lead.stage, st)} />
            <LiveLine move={lead.move} testId="referrer-case-live" />
            <Divider />
            <div className="case-figures" data-testid="referrer-case-strip">
              <div>
                <div className="rail-t" style={{ marginBottom: 7 }}>Facility sought</div>
                <div className="money" style={{ fontSize: 22 }}>{gbp(lead.amount_gbp)}</div>
              </div>
              <div>
                <div className="rail-t" style={{ marginBottom: 7 }}>Best offer on the table</div>
                {best ? (
                  <>
                    <div className="money" style={{ fontSize: 22 }}>{gbp(best.amount_gbp || lead.amount_gbp)}</div>
                    <div className="money-sub">
                      {[best.rate_pct != null ? `${best.rate_pct}%` : null, best.term_months ? `over ${best.term_months} months` : null]
                        .filter(Boolean).join(" ") || best.alias}
                    </div>
                  </>
                ) : <div className="money muted" style={{ fontSize: 22 }}>Nothing yet</div>}
              </div>
              <div>
                <div className="rail-t" style={{ marginBottom: 7 }}>Desks holding it</div>
                <div className="money" style={{ fontSize: 22 }}>{live.length} of {desks.length || 0}</div>
                {desks.length
                  ? <DeskLetters desks={desks} testId="referrer-case-desks" />
                  : <div className="money-sub">Being packaged by Connexis</div>}
              </div>
              {agent ? (
                <div data-testid="referrer-case-submitted-by">
                  <div className="rail-t" style={{ marginBottom: 7 }}>Put in by</div>
                  <div className="money" style={{ fontSize: 22 }}>You</div>
                  <div className="money-sub">on behalf of {me?.company || me?.name || "your company"}</div>
                </div>
              ) : (
                <div>
                  <div className="rail-t" style={{ marginBottom: 7 }}>Your fee if it completes</div>
                  <div className={`money${p?.state === "funded" ? " green" : ""}`} style={{ fontSize: 22 }}>{gbp(p?.likely_gbp)}</div>
                  <div className="money-sub">{projection?.model?.rate || "your agreed share"}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {hasCheck && (
          <div style={{ marginTop: 22 }}>
            <CompanyCheckCard cc={cc} role="referrer" onOpen={openReport} testId="referrer-company-check" />
          </div>
        )}

        {!isAdmin && !declinedOrDone && <CaseFastDocs lead={lead} files={files} setFiles={setFiles} reload={reload} />}

        <div style={{ marginTop: 22 }}>
          <UpdateHistory lanes={desks.map((d) => ({ name: d.alias, updates: d.updates }))} aud="intro" testId="referrer-case-updates" />
        </div>

        {declined && (
          <div className="warn" style={{ marginTop: 22 }} data-testid="referrer-lead-declined">
            <span>
              <b>Every desk we sent this to has passed.</b>{" "}
              {passed.filter((d) => !d.withdrawn).map((d) => d.decline_reason).filter(Boolean).join(" · ") || "No reasons were recorded."}{" "}
              Nothing has been said to your client — that call is yours. We&apos;re looking at alternatives.
            </span>
          </div>
        )}

        {withdrawn.length > 0 && !declined && (
          <div className="warn" style={{ marginTop: 22 }} data-testid="referrer-lead-withdrawn">
            <span>
              <b>We&apos;ve taken this off {withdrawn.length === 1 ? "a desk" : `${withdrawn.length} desks`} that went quiet.</b>{" "}
              {withdrawn.map((d) => d.withdrawn_reason).filter(Boolean).join(" · ")}. They did not decline it —
              they simply did not come back, so we stopped waiting and are placing it elsewhere.
            </span>
          </div>
        )}

        <div style={{ marginTop: 34, scrollMarginTop: 80 }} id="referrer-case-tabs">
          <Tabs
            value={tab}
            onChange={setTab}
            testPrefix="referrer-case-tab"
            items={[
              { key: "thread", label: "Thread" },
              { key: "overview", label: "Overview" },
              ...(hasCheck ? [{ key: "company", label: "Company check" }] : []),
              { key: "documents", label: "Documents", count: files.length },
            ]}
          />
        </div>

        {tab === "company" && hasCheck && <CompanyCheckReport cc={cc} testId="referrer-company-report" />}

        {tab === "thread" && (
          <Panel
            title="Everything on this case, in order"
            sub="Notes, desk messages, documents and every decision — one feed, so nothing goes quiet in somebody's inbox. Drag a document anywhere onto it."
            testId="referrer-case-thread"
          >
            <CaseThread leadId={lead.id} testPrefix="referrer-thread" />
          </Panel>
        )}

        {tab === "overview" && (
          <>
            <Panel
              title="The panel on this case"
              sub={panel?.masking || "Desk letters are fixed for this case and mean nothing on the next one."}
              right={<St tone="blue">{live.length} live</St>}
              pad={false}
              testId="referrer-case-panel"
              footer={<span>Desk letters are fixed for this case and mean nothing on your next one. Nobody&apos;s name, email or phone number ever appears here.</span>}
            >
              {desks.length === 0 ? (
                <Empty testId="referrer-case-panel-empty">Not with a desk yet — the Connexis desk is packaging it.</Empty>
              ) : (
                <Tb head={<><Th>Desk</Th><Th>Status</Th><Th>Last movement</Th><Th className="r">What they said</Th></>}>
                  {desks.map((d, i) => (
                    <tr key={d.alias} data-testid={`referrer-desk-row-${i}`}>
                      <td className="lead"><DeskAlias alias={d.alias} kind={d.kind} index={i} /></td>
                      <Td><StatusChip state={laneStage(d)} testId={`referrer-desk-stage-${i}`} /></Td>
                      <Td>
                        <DateCell>{ukDayTime(d.move?.at || d.step_at || d.status_updated_at || d.sent_at, "—")}</DateCell>
                        {d.move && <CellSub data-testid={`referrer-desk-move-${i}`}>{d.move.text}</CellSub>}
                      </Td>
                      <Td className="r">
                        {d.status === "offer_made" && d.offer ? (
                          <div style={laneStage(d).key === "offer_declined" ? { opacity: 0.6 } : undefined}>
                            <div className="money" style={laneStage(d).key === "offer_declined" ? { textDecoration: "line-through" } : undefined}>
                              {[d.offer.rate_pct != null ? `${d.offer.rate_pct}%` : null, d.offer.term_months ? `${d.offer.term_months} months` : null]
                                .filter(Boolean).join(" / ") || "Terms issued"}
                            </div>
                            {laneStage(d).key === "offer_declined"
                              ? <div className="money-sub" data-testid={`referrer-desk-offer-declined-${i}`}>Turned down by your client</div>
                              : d.offer.expires_on && <div className="money-sub">Valid to {ukDayYear(d.offer.expires_on, d.offer.expires_on)}</div>}
                          </div>
                        ) : (
                          <CellSub style={{ margin: 0 }}>{said(d)}</CellSub>
                        )}
                      </Td>
                    </tr>
                  ))}
                </Tb>
              )}
            </Panel>

            {offers.length > 0 && (
              <Sec title="Terms on the table" sub="Side by side, so your client can compare like for like without ringing round.">
                <div className="card">
                  <Tb head={<><Th>Desk</Th><Th>Facility</Th><Th>Rate</Th><Th>Term</Th><Th>Fees</Th><Th className="r">Expires</Th></>}>
                    {offers.map((o, i) => (
                      <tr key={o.alias} data-testid={`referrer-offer-row-${i}`}>
                        <td className="lead"><DeskAlias alias={o.alias} index={i} /></td>
                        <Td><Money v={o.amount_gbp || lead.amount_gbp} /></Td>
                        <Td><Money raw={o.rate_pct != null ? `${o.rate_pct}%` : "—"} /></Td>
                        <Td><Money raw={o.term_months ? `${o.term_months} months` : "—"} /></Td>
                        <Td><Money v={o.fee_gbp} sub={o.fee_gbp ? "Arrangement" : null} /></Td>
                        <Td className="r">
                          {o.expires_on ? <Money raw={o.expires_on} /> : <Conf level="m">Not stated</Conf>}
                        </Td>
                      </tr>
                    ))}
                  </Tb>
                </div>
              </Sec>
            )}

            <Sec>
              <div className="grid2">
                <Panel title="The business">
                  <Kv rows={[
                    ["Trading name", lead.business_name || lead.applicant_name],
                    ["Company number", lead.company_number || "—"],
                    ["Incorporated", lead.incorporation_date || "—"],
                    ["Sector", lead.industry_sector || "—"],
                    ["Turnover", lead.annual_revenue_gbp ? gbp(lead.annual_revenue_gbp) : "—"],
                    ["Net profit", lead.net_profit_gbp != null ? gbp(lead.net_profit_gbp) : "—"],
                  ]} />
                </Panel>
                <Panel title="Your contact there">
                  <div className="cell-main" style={{ fontSize: 16 }}>{lead.applicant_name}</div>
                  <CellSub>{lead.applicant_position || "—"}</CellSub>
                  <Divider style={{ margin: "18px 0" }} />
                  <Kv rows={[
                    ["Email", <span style={{ fontSize: 13 }} key="e">{lead.applicant_email || "—"}</span>],
                    ["Phone", lead.applicant_phone || "—"],
                    ["Introduced", ukDayYear(lead.created_at, "—")],
                  ]} />
                </Panel>
              </div>
            </Sec>
          </>
        )}

        {tab === "documents" && (
          <>
            <Panel title={`Documents (${files.length})`}
                   sub="Bank statements and filed accounts get decisions back in hours instead of days."
                   testId="referrer-case-documents">
              {files.length > 0
                ? <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{files.map((f) => <FileRow key={f.id} file={f} />)}</div>
                : <div style={{ fontSize: 13, color: "var(--mut)" }}>Nothing attached yet.</div>}
              {files.length < 10 && !isAdmin && (
                <div style={{ marginTop: 18 }}>
                  <DocUploader files={newFiles} onChange={setNewFiles} max={Math.max(1, 10 - files.length)} testId="referrer-detail-uploader" />
                </div>
              )}
              {newFiles.length > 0 && (
                <Btn kind="primary" block style={{ marginTop: 12 }} onClick={attach} disabled={attaching} testId="referrer-attach-docs">
                  {attaching ? "Attaching…" : `Attach ${newFiles.length} document${newFiles.length === 1 ? "" : "s"} to this case`}
                </Btn>
              )}
            </Panel>

            <Sec title="Missing documents" sub="Anything the desk or a lender has asked for shows here. Chase your client and they get a private upload link.">
              <div className="card"><div className="card-b" data-testid="referrer-case-docreqs">
                <DocRequests leadId={lead.id} role="referrer" testPrefix="referrer-docreq" />
              </div></div>
            </Sec>
          </>
        )}
      </PageBody>
    </>
  );
}

/** The three documents lenders ask for first — shown while any is missing when the case opens. */
function CaseFastDocs({ lead, files, setFiles, reload }) {
  const [show] = useState(() => fastDocsMissing(lead.finance_type, lead.files));
  if (!show) return null;
  return (
    <div style={{ marginTop: 22 }}>
      <FastDocs financeType={lead.finance_type} files={files} testId="referrer-fast-docs"
                foot="Got them from your client? Drop them in here. Or chase them from Missing documents on the Documents tab — your client gets a private upload link."
                onUpload={async (label, file, onProgress) => {
                  const rec = await sendFile("/hub/files/upload", file, { label }, onProgress);
                  const r = await api.post(`/refer/leads/${lead.id}/files`, { file_ids: [rec.id] });
                  setFiles(r.data.files || []);
                  reload?.();
                }} />
    </div>
  );
}

function FileRow({ file }) {
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/hub/files/${file.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: r.data.type || file.content_type }));
      const a = document.createElement("a"); a.href = url; a.download = file.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Download failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="lk" style={{ marginBottom: 0, cursor: "default" }} data-testid={`referrer-file-${file.id}`}>
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
        {file.filename}
        <span style={{ color: "var(--mut)", fontWeight: 600 }}> · {prettySize(file.size)}</span>
      </span>
      <button onClick={dl} disabled={busy} aria-label="Download"
              style={{ border: 0, background: "none", cursor: "pointer", color: "var(--mut)", display: "grid", placeItems: "center" }}>
        <I.download />
      </button>
    </div>
  );
}
